/**
 * AskX1Chart.tsx — renders a deterministic chart payload produced by the backend
 * (server/services/chat/chartExtractors.js) inline under an Ask X1 answer. [Phase 3]
 *
 * Payload is derived from REAL tool data — this component only draws it.
 *  - candlestick / line → lightweight-charts v5 (already bundled)
 *  - bar (categorical)  → lightweight CSS bars (cleaner for labelled categories)
 */
import { useEffect, useRef } from 'react'
import { createChart, ColorType, CandlestickSeries, LineSeries, type IChartApi } from 'lightweight-charts'

export type ChartKind = 'candlestick' | 'line' | 'bar'
export interface ChartPayload {
  kind: ChartKind
  title?: string
  xLabel?: string
  yLabel?: string
  unit?: string
  series: any[]
  /** grouped/paired-bar payload (compare_entities) — optional, backward compatible */
  groups?: Array<{ label: string; unit?: string; items: Array<{ label: string; value: number }> }>
}

function isDark(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
}

function fmtVal(v: number): string {
  const a = Math.abs(v)
  if (a !== 0 && a < 0.001) return v.toExponential(2)
  if (a >= 1000) return v.toLocaleString(undefined, { maximumFractionDigits: 0 })
  return v.toLocaleString(undefined, { maximumFractionDigits: a < 1 ? 4 : 2 })
}

function TvChart({ chart }: { chart: ChartPayload }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el || !chart.series?.length) return
    const dark = isDark()
    let instance: IChartApi | null = createChart(el, {
      height: 200,
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: dark ? '#a3a3a3' : '#737373',
        fontSize: 10,
        attributionLogo: false, // we don't use TradingView — hide the library's attribution logo
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false, timeVisible: true },
      handleScroll: false,
      handleScale: false,
      crosshair: { horzLine: { visible: false }, vertLine: { labelVisible: false } },
    })

    try {
      if (chart.kind === 'candlestick') {
        const s = instance.addSeries(CandlestickSeries, {
          upColor: dark ? '#3fd24a' : '#0c8911', downColor: '#ff5f52', borderVisible: false,
          wickUpColor: dark ? '#3fd24a' : '#0c8911', wickDownColor: '#ff5f52',
        })
        s.setData(chart.series.map((p) => ({ time: p.t, open: p.o, high: p.h, low: p.l, close: p.c })))
      } else {
        const s = instance.addSeries(LineSeries, { color: dark ? '#22d3ee' : '#0ea5e9', lineWidth: 2, priceLineVisible: false })
        s.setData(chart.series.map((p) => ({ time: p.t, value: p.value })))
      }
      instance.timeScale().fitContent()
    } catch {
      /* malformed series — leave the empty frame rather than crash the chat */
    }

    return () => { instance?.remove(); instance = null }
  }, [chart])

  return (
    <div className="mt-2">
      {chart.title && <div className="mb-1 text-[11px] font-medium text-secondary-500 dark:text-secondary-400">{chart.title}</div>}
      <div ref={ref} style={{ height: 200 }} className="w-full" />
    </div>
  )
}

// Grouped/paired bars for a two-entity comparison (compare_entities). Each metric is its own row,
// normalized WITHIN that metric (so wildly different scales — 1.2M stake vs 10% commission — each
// read fairly), with both entities colour-coded and their real values shown. Backward compatible:
// only used when the payload carries `groups`; otherwise the flat BarChart renders.
interface CompareGroup { label: string; unit?: string; items: Array<{ label: string; value: number }> }
function GroupedBarChart({ chart, groups }: { chart: ChartPayload; groups: CompareGroup[] }) {
  const colors = ['bg-primary-500', 'bg-amber-500']
  const legend = groups[0]?.items || []
  return (
    <div className="mt-2">
      {chart.title && <div className="mb-1.5 text-[11px] font-medium text-secondary-500 dark:text-secondary-400">{chart.title}</div>}
      <div className="mb-2 flex flex-wrap gap-3 text-[11px]">
        {legend.map((it, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className={`inline-block h-2 w-2 rounded-sm ${colors[i % colors.length]}`} />
            <span className="max-w-[9rem] truncate text-secondary-600 dark:text-secondary-300" title={String(it.label)}>{it.label}</span>
          </span>
        ))}
      </div>
      <div className="space-y-2.5">
        {groups.map((g, gi) => {
          const max = Math.max(...g.items.map((it) => Math.abs(Number(it.value) || 0)), 0) || 1
          const unit = g.unit && g.unit !== 'USD' ? g.unit : ''
          return (
            <div key={gi}>
              <div className="mb-0.5 text-[10px] uppercase tracking-wide text-secondary-400 dark:text-secondary-500">{g.label}</div>
              <div className="space-y-1">
                {g.items.map((it, ii) => (
                  <div key={ii} className="flex items-center gap-2 text-[11px]">
                    <div className="h-3 flex-1 overflow-hidden rounded bg-secondary-100 dark:bg-secondary-800">
                      <div className={`h-full rounded ${colors[ii % colors.length]}`} style={{ width: `${Math.max(2, (Math.abs(Number(it.value) || 0) / max) * 100)}%` }} />
                    </div>
                    <span className="w-24 shrink-0 text-right tabular-nums text-secondary-700 dark:text-secondary-200">
                      {fmtVal(Number(it.value) || 0)}{unit === '%' ? '%' : unit ? ` ${unit}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function BarChart({ chart }: { chart: ChartPayload }) {
  const groups = (chart as any).groups as CompareGroup[] | undefined
  if (Array.isArray(groups) && groups.length) return <GroupedBarChart chart={chart} groups={groups} />
  const max = Math.max(...chart.series.map((s) => Number(s.value) || 0), 0) || 1
  const unit = chart.unit && chart.unit !== 'USD' ? chart.unit : ''
  return (
    <div className="mt-2">
      {chart.title && <div className="mb-1.5 text-[11px] font-medium text-secondary-500 dark:text-secondary-400">{chart.title}</div>}
      <div className="space-y-1">
        {chart.series.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-[11px]">
            <span className="w-16 shrink-0 truncate text-secondary-500 dark:text-secondary-400" title={String(s.label)}>{s.label}</span>
            <div className="h-3 flex-1 overflow-hidden rounded bg-secondary-100 dark:bg-secondary-800">
              <div className="h-full rounded bg-primary-500" style={{ width: `${Math.max(2, (Number(s.value) / max) * 100)}%` }} />
            </div>
            <span className="w-16 shrink-0 text-right tabular-nums text-secondary-700 dark:text-secondary-200">
              {fmtVal(Number(s.value))}{unit === '%' ? '%' : unit ? ` ${unit}` : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function AskX1Chart({ chart }: { chart: ChartPayload }) {
  if (!chart || !Array.isArray(chart.series) || chart.series.length < 2) return null
  return chart.kind === 'bar' ? <BarChart chart={chart} /> : <TvChart chart={chart} />
}
